import { ReactNode } from 'react'
import cx from 'classnames'
import { Stack, Text } from '~m/designSystem'
import styles from './SourceCard.module.css'

export interface SourceCardLine {
  /** Optional icon shown before the line text */
  icon?: ReactNode
  /** The line content, e.g. an email or phone number */
  text: ReactNode
}

export interface SourceCardProps {
  /** The record's primary name/title */
  name: ReactNode
  /** Detail lines shown under the name */
  lines?: SourceCardLine[]
  /** Optional leading visual, e.g. a PersonAvatar */
  avatar?: ReactNode
  /** Additional CSS class */
  className?: string
}

/**
 * A read-only summary of an incoming record from an external
 * source (the left-hand side of a reconciliation row). Purely
 * composed from Text and Stack so it inherits the app's type scale.
 */
export function SourceCard({
  name,
  lines = [],
  avatar,
  className,
}: SourceCardProps) {
  return (
    <Stack
      direction="row"
      gap={1.5}
      className={cx(styles.SourceCard, className)}
    >
      {avatar}
      <Stack gap={0.5} className={styles.SourceCard__body}>
        <Text
          variant="section-title"
          component="p"
          data-source-name
        >
          {name}
        </Text>
        {lines.length > 0 && (
          <Stack component="span" gap={0.5}>
            {lines.map((line, i) => (
              <Stack
                key={i}
                component="span"
                direction="row"
                alignItems="center"
                gap={0.5}
              >
                {line.icon}
                <Text variant="date" component="span">
                  {line.text}
                </Text>
              </Stack>
            ))}
          </Stack>
        )}
      </Stack>
    </Stack>
  )
}
